import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMeQuery } from '@platform/state/api';
import { Icon } from '@platform/ui';
import { routes } from '@platform/core/lib/routes';
import { useInbox } from '@platform/features/users/inbox/useInbox';
import { useOpenInboxEntry } from '@platform/features/users/inbox/useOpenInboxEntry';
import type { IInboxEntry } from '@platform/features/users/inbox/IInboxEntry';
import type { INotificationsMenuProps } from './INotificationsMenuProps';
import { MenuOverlay } from './MenuOverlay';
import { NotificationRow } from './NotificationRow';
import { useMenuKeys } from './useMenuKeys';

const SHOWN = 8;

/**
 * Сповіщення (UIUX top-bar, patterns/notifications-inbox, прототип v4 G:L241–272): кнопка з дзвіночком і жовтим лічильником непрочитаних
 * збережених сповіщень (як unreadCount прототипу; оновлюються щохвилини); меню 360px — «Вхідні · Останні повідомлення · усі робочі простори», «Позначити все
 * прочитаним», «Відкрити вхідні →», 8 найновіших елементів обчислених вхідних (доручення, нагадування про термін, коментарі, оновлення),
 * «Ви все опрацювали.», унизу «Відкрити вхідні →» і «Показано 8 з N». Меню прив'язане до правої групи панелі (праворуч — край аватара,
 * 16px від краю вікна, як у прототипі); відкрите меню не підсвічує дзвіночок.
 */
export function NotificationsMenu({ ws, open, onToggle, onClose }: INotificationsMenuProps) {
  const { t } = useTranslation();
  const button = useRef<HTMLButtonElement>(null);
  const { menu, onKeyDown } = useMenuKeys<HTMLDivElement>(open, onClose, button);
  const { data: me } = useMeQuery();
  const { entries, storedUnread: unread, setRead, markAllRead } = useInbox();
  const openEntry = useOpenInboxEntry(ws, setRead);
  const title = unread ? t('shell.notificationsUnread', { count: unread }) : t('shell.notifications');
  const wsName = (e: IInboxEntry) => me?.workspaces.find((w) => w.id === e.workspaceId)?.name ?? e.workspaceName ?? t('shell.platform');
  return (
    <div className="tb-anchor tb-anchor--static">
      <button ref={button} type="button" className="po-topbar__icon tb-icon tb-bell" title={title} aria-label={title} aria-haspopup="menu" aria-expanded={open} onClick={onToggle}>
        <Icon name="bell" size={16} />
        {unread > 0 && <span className="po-topbar__badge">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <>
          <MenuOverlay onClose={onClose} />
          <div ref={menu} role="menu" aria-label={t('shell.inbox')} className="tb-dd tb-dd--right tb-notif" onKeyDown={onKeyDown}>
            <div className="tb-notif__head">
              <span className="tb-notif__titles"><strong>{t('shell.inbox')}</strong><span className="tb-notif__sub">{t('shell.inboxSub')}</span></span>
              <span className="tb-notif__actions">
                <button type="button" role="menuitem" className="tb-link" onClick={markAllRead}>{t('shell.markAllRead')}</button>
                <Link role="menuitem" className="tb-link is-strong" to={routes.inbox()} onClick={onClose}>{t('shell.openInbox')}</Link>
              </span>
            </div>
            <div className="tb-notif__list">
              {entries.slice(0, SHOWN).map((e) => <NotificationRow key={e.key} entry={e} workspaceName={wsName(e)} onOpen={(x) => { onClose(); openEntry(x); }} />)}
              {!entries.length && <div className="tb-notif__empty">{t('shell.caughtUp')}</div>}
            </div>
            <Link role="menuitem" className="tb-notif__foot" to={routes.inbox()} onClick={onClose}>
              {t('shell.openInbox')}
              {entries.length > 0 && <span className="tb-notif__more">{t('shell.showingOf', { shown: Math.min(SHOWN, entries.length), count: entries.length })}</span>}
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
